import { Box, Icon, Theme, Typography } from "@outschool/backpack";
import { faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React, { useEffect } from "react";

import { useBannerDismissal } from "../../hooks/useBannerDismissal";

const ART_SHOWCASE_URL = "https://outschool.com/campaigns/visual-arts-showcase";
const BANNER_NAME = "art_showcase";
const BANNER_START_DATE = "2026-03-02";
const BANNER_END_DATE = "2026-05-15";

export function IsValidTimeRangeForArtShowcaseBanner() {
  const now = dayjs();
  const startDate = dayjs(BANNER_START_DATE);
  const endDate = dayjs(BANNER_END_DATE);

  return now.isSameOrAfter(startDate, "day") && now.isBefore(endDate, "day");
}

export default function ArtShowcaseBanner() {
  const { t } = useTranslation(
    "client\\components\\banners\\ArtShowcaseBanner",
  );
  const navigate = useNavigation();
  const trackEvent = useTrackEvent();
  const isMobile = useIsMobile();
  const { dismissBanner, isBannerDismissed } = useBannerDismissal();

  // Track if banner view has been tracked for this mount to prevent duplicate tracking
  const hasTrackedViewRef = React.useRef<boolean>(false);

  useEffect(() => {
    if (!hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      trackEvent("art_showcase_banner_view", {
        is_mobile: isMobile,
      });
    }
  }, [trackEvent, isMobile]);

  const handleBannerClick = () => {
    trackEvent("art_showcase_banner_click", {
      is_mobile: isMobile,
    });
    navigate(ART_SHOWCASE_URL, {
      hardNav: true,
      newTab: true,
    });
  };

  const handleDismiss = (event: React.MouseEvent) => {
    event.stopPropagation();
    trackEvent("art_showcase_banner_dismiss", {
      is_mobile: isMobile,
    });
    dismissBanner(BANNER_NAME);
  };

  if (
    !IsValidTimeRangeForArtShowcaseBanner() ||
    isBannerDismissed(BANNER_NAME)
  ) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary[600],
        minHeight: "50px",
        cursor: "pointer",
        padding: "0 1em",
        [theme.breakpoints.down("md")]: {
          padding: "20px",
        },
      })}
      onClick={handleBannerClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "1440px",
          margin: "0 auto",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              {t(
                "🎨 Showcase your kid’s artistic talent and win up to $1,000 in prize value!",
              )}
              <Box
                component="span"
                sx={{
                  marginLeft: 8,
                  textDecoration: "underline",
                }}
              >
                {t("Enter Now")}
              </Box>
            </Typography>
          </Box>
        </Box>
        <Icon
          icon={faTimes}
          onClick={handleDismiss}
          aria-label={t("Close banner")}
          sx={(theme: Theme) => ({
            color: theme.palette.common.white,
            position: "absolute",
            right: 0,
            width: "2rem",
            height: "2rem",
            cursor: "pointer",
          })}
        />
      </Box>
    </Box>
  );
}
